import { useCan } from '@/components/can/Can';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { AddHostGalleryForm } from '@/features/store-management/basic-stores/host-management/gallery-management/AddHostGalleryForm';
import { useDropdown } from '@/hooks/useDropdown';
import { useDynamicLocale } from '@/hoslog-intl';
import { splitLocalizedName } from '@/lib/splitHostName';
import { additionalValue, Pagination } from '@/react-query/context/ApiClientContextController';
import { useMutation } from '@/react-query/hooks/useMutation/useMutation';
import { GalleryManagement } from '@/services/galleryManagement/galleryManagement.types';
import { revalidate } from '@/services/revalidate/revalidate';
import { useParams } from '@tanstack/react-router';
import * as React from 'react';
import { toast } from 'sonner';
import { getColumns } from './Column';
import { useQueryClient } from '@tanstack/react-query';

export function HostNameGalleryTable({
  data,
  isLoading,
  pagination,
  commonHostId,
}: {
  data: GalleryManagement[];
  isLoading: boolean;
  pagination: Pagination;
  commonHostId: additionalValue;
}) {
  const { openConfirmation } = useConfirmationStore();
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation('deleteHostGallery', {
    onSuccess: async (res) => {
      toast.success(res.message);
      await revalidate(['hostDetail', 'clubHosts', 'clubBasicInfo', 'all']);
      await queryClient.invalidateQueries({ queryKey: ['galleryList'] });
    },
    onError: (res) => {
      toast.error(res.message);
    },
  });

  const { onOpen } = useDialogFormStore();
  const { t, locale } = useDynamicLocale();
  const params = useParams({
    from: '/(app)/store-information/premium-stores/host-management/$clubid/gallery/$host-gallery',
  });
  const clubId = params['clubid'] || '';
  const hostId = params['host-gallery'] || '';
  const canAddGallery = useCan({ allOf: [PERMS.gallery.premium.create] });
  const columns = React.useMemo(
    () => getColumns(mutateAsync, openConfirmation, onOpen, t),
    [mutateAsync, openConfirmation, onOpen, t],
  );
  const { data: options } = useDropdown(
    'hostName',
    commonHostId?.commonHostId,
    undefined,
    undefined,
    Boolean(commonHostId?.commonHostId),
  );
  const { japanese, english } = splitLocalizedName(options?.[0]?.label);
  const hostName = locale === 'en' ? english : japanese;
  const onAddNew = () => {
    onOpen({
      modalTitle: 'storeManagement.galleryManagement.page.addGallery.manageClubImage',
      title: 'Manage HostNameGallery',
      okText: t('storeManagement.basicStores.galleryManagement.button.submit'),
      size: 'lg',
      component: <AddHostGalleryForm clubId={clubId} hostId={hostId} />,
    });
  };

  const { table } = useDataTable({
    data,
    columns,
    pageCount: 0,
    initialState: {
      sorting: [{ id: 'id', desc: true }],
    },
    meta: {
      title: `${hostName} ${t('storeManagement.galleryManagement.page.table.title')}`,
      action: onAddNew,
      actionKey: canAddGallery ? 'onAddNew' : '',
      loading: isLoading,
      totalPage: pagination.totalItems,
    },
    getRowId: (originalRow) => String(originalRow.id),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table}>
        <DataTableToolbar />
      </DataTable>
    </>
  );
}
